/**
 * Authentication API service
 */
import { apiClient, createUrlEncodedData, setToken, removeToken } from './client';
import type { AuthResponse, Language, LoginCredentials, RegisterCredentials, User, MessageResponse } from '@/types';

export const authApi = {
  /**
   * Register a new user. `language` is the app language: the confirmation
   * email is written in it.
   */
  register: async (credentials: RegisterCredentials, language: Language): Promise<AuthResponse> => {
    const data = createUrlEncodedData({
      email: credentials.email,
      password: credentials.password,
      name: credentials.name,
      lang: language,
    });

    const response = await apiClient.post<AuthResponse>('/auth/register', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    // Store token on successful registration
    setToken(response.data.access_token);
    return response.data;
  },

  /**
   * Login with email and password
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const data = createUrlEncodedData({
      email: credentials.email,
      password: credentials.password,
    });

    const response = await apiClient.post<AuthResponse>('/auth/login', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    // Store token on successful login
    setToken(response.data.access_token);
    return response.data;
  },

  /**
   * Logout - clear token
   */
  logout: (): void => {
    removeToken();
  },

  /**
   * Get Google OAuth URL
   */
  getGoogleAuthUrl: async (): Promise<{ url: string }> => {
    const response = await apiClient.get<{ url: string }>('/auth/google-url');
    return response.data;
  },

  /**
   * Exchange the one-time code of the Google return (`/?google_code=`) for the session token
   * (SEC3: the token never travels in a URL). Valid 60 seconds, once.
   */
  exchangeGoogleCode: async (code: string): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>('/auth/google/exchange', { code });
    setToken(response.data.access_token);
    return response.data;
  },

  /**
   * Request magic link for passwordless auth
   */
  requestMagicLink: async (email: string): Promise<MessageResponse> => {
    const data = createUrlEncodedData({ email });
    const response = await apiClient.post<MessageResponse>('/auth/magic-link/request', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  /**
   * Verify magic link token
   */
  verifyMagicLink: async (token: string): Promise<AuthResponse> => {
    const data = createUrlEncodedData({ token });
    const response = await apiClient.post<AuthResponse>('/auth/magic-link/verify', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    setToken(response.data.access_token);
    return response.data;
  },

  /**
   * Request password reset email (written in `language`)
   */
  requestPasswordReset: async (email: string, language: Language): Promise<MessageResponse> => {
    const data = createUrlEncodedData({ email, lang: language });
    const response = await apiClient.post<MessageResponse>('/auth/password-reset/request', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  /**
   * Confirm password reset with token and new password
   */
  confirmPasswordReset: async (token: string, newPassword: string): Promise<MessageResponse> => {
    const data = createUrlEncodedData({ token, new_password: newPassword });
    const response = await apiClient.post<MessageResponse>('/auth/password-reset/confirm', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  /**
   * Resend email confirmation (written in `language`)
   */
  resendConfirmationEmail: async (email: string, language: Language): Promise<MessageResponse> => {
    const data = createUrlEncodedData({ email, lang: language });
    const response = await apiClient.post<MessageResponse>('/auth/confirm-email/resend', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/user/me');
    return response.data;
  },
};

export default authApi;
